import React, { useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import AllowedEmailDomainsModal from '../../components/Admin/AllowedEmailDomainsModal'
import { resolveIconNode } from '../../components/icons'

const AUTH_RULES = [
  { id: 'block_personal', icon: 'red', iconKey: 'moderation', name: 'Block Personal Email Domains', desc: 'Only email addresses from the allowed corporate domain list can register.', status: '0 domains', default: true },
  { id: 'email_verify', icon: 'green', iconKey: 'email', name: 'Email Verification Required', desc: 'Send verification email with confirmation link. User must verify ownership.', default: true },
  { id: 'domain_match', icon: 'blue', iconKey: 'fund', name: 'Domain-Company Match Check', desc: 'Compare email domain vs company website domain. Flag mismatches.', default: true },
  { id: 'manual_review', icon: 'purple', iconKey: 'users', name: 'Manual Review Required', desc: 'All registrations require admin approval before account activation.', default: true },
  { id: 'login_code', icon: 'green', iconKey: 'lock', name: 'Login Verification Code', desc: '4-digit code sent to registered work email on every login. Valid for 5 minutes.', default: true },
  { id: 'login_bypass_week', icon: 'blue', iconKey: 'clock', name: 'Skip Email Code Within 7 Days', desc: 'Users who logged in within the past 7 days can sign in without a new email verification code.', default: true },
  { id: 'login_slider', icon: 'blue', iconKey: 'shield', name: 'Login Slider Captcha', desc: 'Require drag-to-verify slider on the login page before sending codes or signing in.', default: true },
  { id: 'login_master_8888', icon: 'orange', iconKey: 'key', name: 'Allow Master Code 8888', desc: 'When enabled, 8888 is accepted as a universal login verification code (dev / emergency access).', default: false },
  { id: 'admin_lockout_exempt', icon: 'orange', iconKey: 'shield', name: 'Exempt Admin Accounts from Login Lockout', desc: 'When enabled, accounts with the Admin role are never locked after repeated failed password attempts.', default: true },
  { id: 'chat_login_nudge', icon: 'green', iconKey: 'email', name: 'Inactive User Chat Email Reminder', desc: 'If a recipient has not logged in for 24+ hours after receiving a private message, email them to sign in and read it.', default: true },
  { id: 'chat_read_receipts', icon: 'blue', iconKey: 'chat', name: 'Chat Read Receipts', desc: 'Show read/unread status on sent messages in private and group chats (like DingTalk).', default: true },
  { id: 'chat_online_count', icon: 'green', iconKey: 'users', name: 'Chat Online Count Display', desc: 'Show the total online user count badge on the Chat page header.', default: true },
]

const SESSION_FIELDS = [
  { id: 'sessionTimeout', label: 'Session Timeout', value: 120, hint: 'minutes of inactivity' },
  { id: 'maxConcurrentSessions', label: 'Max Concurrent Sessions', value: 10, hint: 'per user account' },
  { id: 'loginAttemptLimit', label: 'Login Attempt Limit', value: 5, hint: 'before account lockout' },
  { id: 'lockoutDuration', label: 'Lockout Duration', value: 30, hint: 'minutes' },
]

const RULE_SETTINGS_KEY = {
  block_personal: 'blockPersonalEmail',
  email_verify: 'emailVerifyRequired',
  domain_match: 'domainCompanyMatch',
  manual_review: 'manualReviewRequired',
  login_code: 'loginVerificationCode',
  login_bypass_week: 'loginCodeRecentLoginBypass',
  login_slider: 'loginSliderCaptcha',
  login_master_8888: 'loginMasterCode8888',
  admin_lockout_exempt: 'adminLoginLockoutExempt',
  chat_login_nudge: 'chatInactiveLoginEmail',
  chat_read_receipts: 'chatReadReceipts',
  chat_online_count: 'chatOnlineCountDisplay',
  ip_admin: 'ipWhitelistAdmin',
  geo_block: 'geoBlocking',
  encryption: 'encryption',
  watermark: 'watermark',
  audit_log: 'auditLog',
}

const ACCESS_RULES = [
  { id: 'ip_admin', icon: 'orange', iconKey: 'pin', name: 'IP Whitelist for Admin', desc: 'Admin accounts can only login and access admin APIs from whitelisted IP addresses.', default: false },
  { id: 'geo_block', icon: 'blue', iconKey: 'globe', name: 'Geo-Blocking', desc: 'Block API access from specific countries (ISO codes). Uses CF-IPCountry or X-Country-Code header.', default: false },
]

const DATA_RULES = [
  { id: 'encryption', icon: 'green', iconKey: 'security', name: 'Message Encryption at Rest', desc: 'Encrypt private and group chat message content in the database (AES-256-GCM).', default: true },
  { id: 'watermark', icon: 'purple', iconKey: 'clipboard', name: 'Document Watermarking', desc: 'Apply user-specific watermarks when downloading confidential PDF/image files.', default: true },
  { id: 'audit_log', icon: 'blue', iconKey: 'chart', name: 'Audit Logging', desc: 'Log admin actions, file downloads, and authentication events.', default: true },
]

const TOGGLEABLE_RULE_IDS = new Set(
  [...AUTH_RULES, ...ACCESS_RULES, ...DATA_RULES]
    .filter((r) => r.default !== undefined)
    .map((r) => r.id)
)

const FLOW_STEPS = [
  { title: 'Allowed Corporate Email Domains', desc: 'Only email addresses from the admin-managed allowed domain list can register.' },
  { title: 'Send Verification Email', desc: 'Send email with confirmation link and request company name, job title, website.' },
  { title: 'Domain-Company Match Check', desc: 'Compare email domain with company website domain. Mismatches flagged for review.' },
  { title: 'Manual Review Queue', desc: 'All registrations go to Registration Review for admin approval.' },
  { title: 'Admin Approve / Reject', desc: 'Approved users set password and complete profile. Rejected users receive notification.' },
]

function RuleRow({ rule, checked, onToggle, action }) {
  return (
    <div className="admin-security-rule-item">
      <div className="admin-security-rule-left">
        <div className={`admin-security-rule-icon ${rule.icon}`}>{resolveIconNode(rule.iconKey, { size: 'md' })}</div>
        <div>
          <div className="admin-security-rule-name">{rule.name}</div>
          <div className="admin-security-rule-desc">{rule.desc}</div>
        </div>
      </div>
      <div className="admin-security-rule-right">
        {rule.status && <span className="admin-security-rule-status on">{rule.status}</span>}
        {checked !== undefined && (
          <span className={`admin-security-rule-status ${checked ? 'on' : 'off'}`}>{checked ? 'Active' : (rule.statusOff || 'Disabled')}</span>
        )}
        {checked !== undefined && (
          <label className="admin-toggle">
            <input type="checkbox" checked={!!checked} onChange={() => onToggle(rule.id)} />
            <span className="admin-toggle-slider" />
          </label>
        )}
        {action}
      </div>
    </div>
  )
}

function parseLines(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

function joinLines(list) {
  return (list || []).join('\n')
}

export default function AdminSecurity() {
  const [summary, setSummary] = useState(null)
  const [words, setWords] = useState([])
  const [rules, setRules] = useState(() => Object.fromEntries(
    [...AUTH_RULES, ...ACCESS_RULES, ...DATA_RULES].filter((r) => r.default !== undefined).map((r) => [r.id, r.default])
  ))
  const [sessionFields, setSessionFields] = useState(() => Object.fromEntries(SESSION_FIELDS.map((f) => [f.id, f.value])))
  const [adminAllowedIpsText, setAdminAllowedIpsText] = useState('')
  const [blockedCountriesText, setBlockedCountriesText] = useState('')
  const [newWord, setNewWord] = useState({ pattern: '', label: '', is_regex: true })
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [allowedDomainTotal, setAllowedDomainTotal] = useState(0)
  const [domainModalOpen, setDomainModalOpen] = useState(false)

  const loadAllowedDomainCount = async () => {
    try {
      const res = await axios.get('/api/admin/allowed-email-domains/count')
      setAllowedDomainTotal(Number(res.data?.total || 0))
    } catch {
      /* optional on first load */
    }
  }

  const applySecuritySettings = (sec) => {
    if (!sec) return
    setRules((prev) => {
      const next = { ...prev }
      for (const [ruleId, key] of Object.entries(RULE_SETTINGS_KEY)) {
        if (sec[key] !== undefined) next[ruleId] = !!sec[key]
      }
      return next
    })
    setSessionFields({
      sessionTimeout: sec.sessionTimeout ?? 60,
      maxConcurrentSessions: sec.maxConcurrentSessions ?? 10,
      loginAttemptLimit: sec.loginAttemptLimit ?? 5,
      lockoutDuration: sec.lockoutDuration ?? 30,
    })
    setAdminAllowedIpsText(joinLines(sec.adminAllowedIps))
    setBlockedCountriesText(joinLines(sec.blockedCountryCodes))
    setDirty(false)
  }

  const loadWords = async () => {
    try {
      const wRes = await axios.get('/api/admin/moderation/chat-filter-words')
      setWords(wRes.data)
    } catch {
      toast.error('Failed to load chat filter rules')
    }
  }

  const load = async () => {
    try {
      const [sRes, wRes, settingsRes] = await Promise.all([
        axios.get('/api/admin/security-summary'),
        axios.get('/api/admin/moderation/chat-filter-words'),
        axios.get('/api/admin/cms/platform-settings'),
      ])
      setSummary(sRes.data)
      setWords(wRes.data)
      applySecuritySettings(settingsRes.data?.security)
    } catch {
      toast.error('Failed to load security data')
    }
  }

  useEffect(() => { load(); loadAllowedDomainCount() }, [])

  const markDirty = () => setDirty(true)

  const toggleRule = (id) => {
    setRules((prev) => ({ ...prev, [id]: !prev[id] }))
    markDirty()
  }

  const saveSecuritySettings = async () => {
    setSaving(true)
    const security = {}
    for (const [ruleId, key] of Object.entries(RULE_SETTINGS_KEY)) {
      if (!TOGGLEABLE_RULE_IDS.has(ruleId)) continue
      security[key] = !!rules[ruleId]
    }
    for (const f of SESSION_FIELDS) {
      security[f.id] = sessionFields[f.id]
    }
    security.adminAllowedIps = parseLines(adminAllowedIpsText)
    security.blockedCountryCodes = parseLines(blockedCountriesText).map((c) => c.toUpperCase())
    try {
      const res = await axios.put('/api/admin/cms/platform-settings', { security })
      applySecuritySettings(res.data?.settings?.security)
      window.dispatchEvent(new Event('rs-platform-settings-changed'))
      toast.success('Security settings saved')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const addWord = async (e) => {
    e.preventDefault()
    try {
      await axios.post('/api/admin/moderation/chat-filter-words', newWord)
      toast.success('Rule added')
      setNewWord({ pattern: '', label: '', is_regex: true })
      await loadWords()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add rule')
    }
  }

  const removeWord = async (id) => {
    try {
      await axios.delete(`/api/admin/moderation/chat-filter-words/${id}`)
      toast.success('Rule deleted')
      await loadWords()
    } catch {
      toast.error('Failed to delete rule')
    }
  }

  return (
    <AdminLayout title="Security Rules">
      <div className="admin-security-stat-grid">
        <div className="admin-security-stat">
          <div className="admin-security-stat-value">{summary?.activeRules ?? 0}</div>
          <div className="admin-security-stat-label">Active Rules</div>
        </div>
        <div className="admin-security-stat">
          <div className="admin-security-stat-value">{summary?.twoFaUsers ?? '—'}</div>
          <div className="admin-security-stat-label">2FA Enabled Users</div>
        </div>
        <div className="admin-security-stat">
          <div className="admin-security-stat-value">{summary?.failedAttemptsToday ?? 0}</div>
          <div className="admin-security-stat-label">Failed Login Attempts (Today)</div>
        </div>
        <div className="admin-security-stat">
          <div className="admin-security-stat-value">{summary?.securityScore != null ? `${summary.securityScore}/100` : '—'}</div>
          <div className="admin-security-stat-label">Security Score</div>
        </div>
      </div>

      <div className="admin-alert-banner warning">
        <span>ℹ️</span>
        <div>
          <strong>Note:</strong> All rules on this page are enforced live, including session limits, IP whitelist, geo-blocking, encryption, watermarks, and audit logging.
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header"><span className="admin-card-title">Authentication Rules</span></div>
        <div className="admin-card-body">
          <div className="admin-security-section-title">Work Email Verification Flow</div>
          <div className="admin-security-flow">
            {FLOW_STEPS.map((step, i) => (
              <React.Fragment key={step.title}>
                <div className="admin-security-flow-step">
                  <div className="admin-security-flow-num">{i + 1}</div>
                  <div>
                    <div className="admin-security-flow-title">{step.title}</div>
                    <div className="admin-security-flow-desc">{step.desc}</div>
                  </div>
                </div>
                {i < FLOW_STEPS.length - 1 && <div className="admin-security-flow-arrow">↓</div>}
              </React.Fragment>
            ))}
          </div>
          {AUTH_RULES.map((r) => (
            <RuleRow
              key={r.id}
              rule={
                r.id === 'block_personal'
                  ? { ...r, status: `${allowedDomainTotal.toLocaleString()} domains` }
                  : r
              }
              checked={r.default !== undefined ? rules[r.id] : undefined}
              onToggle={toggleRule}
              action={
                r.id === 'block_personal' ? (
                  <button
                    type="button"
                    className="admin-btn sm secondary"
                    onClick={() => setDomainModalOpen(true)}
                  >
                    Manage Allowed Domains
                  </button>
                ) : null
              }
            />
          ))}
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header"><span className="admin-card-title">⏱️ Session & Access Control</span></div>
        <div className="admin-card-body">
          {SESSION_FIELDS.map((f) => (
            <div key={f.id} className="admin-security-field-row">
              <div className="admin-security-field-label">{f.label}</div>
              <input
                type="number"
                className="admin-security-field-input"
                value={sessionFields[f.id]}
                onChange={(e) => { setSessionFields((p) => ({ ...p, [f.id]: Number(e.target.value) })); markDirty() }}
              />
              <div className="admin-security-field-hint">{f.hint}</div>
            </div>
          ))}
          <div className="admin-security-section-title">IP & Geo Restrictions</div>
          {ACCESS_RULES.map((r) => (
            <RuleRow key={r.id} rule={r} checked={rules[r.id]} onToggle={toggleRule} />
          ))}
          <div className="admin-security-field-row" style={{ alignItems: 'flex-start' }}>
            <div className="admin-security-field-label">Admin Allowed IPs</div>
            <textarea
              className="admin-security-field-input"
              rows={4}
              placeholder={'127.0.0.1\n203.0.113.0/24\n10.0.*'}
              value={adminAllowedIpsText}
              onChange={(e) => { setAdminAllowedIpsText(e.target.value); markDirty() }}
              style={{ minWidth: 280, resize: 'vertical' }}
            />
            <div className="admin-security-field-hint">One IP or CIDR per line. Required when IP whitelist is enabled.</div>
          </div>
          <div className="admin-security-field-row" style={{ alignItems: 'flex-start' }}>
            <div className="admin-security-field-label">Blocked Countries</div>
            <textarea
              className="admin-security-field-input"
              rows={3}
              placeholder={'CN\nRU'}
              value={blockedCountriesText}
              onChange={(e) => { setBlockedCountriesText(e.target.value); markDirty() }}
              style={{ minWidth: 280, resize: 'vertical' }}
            />
            <div className="admin-security-field-hint">ISO 3166-1 alpha-2 codes, one per line.</div>
          </div>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header"><span className="admin-card-title">Data Protection</span></div>
        <div className="admin-card-body">
          {DATA_RULES.map((r) => (
            <RuleRow key={r.id} rule={r} checked={rules[r.id]} onToggle={toggleRule} />
          ))}
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header"><span className="admin-card-title">Chat Filter Rules ({words.length})</span></div>
        <div className="admin-card-body">
          <form onSubmit={addWord} className="admin-filter-bar" style={{ marginBottom: 16 }}>
            <input className="admin-filter-search" placeholder="Pattern" value={newWord.pattern} onChange={(e) => setNewWord((p) => ({ ...p, pattern: e.target.value }))} required />
            <input className="admin-filter-search" placeholder="Label" value={newWord.label} onChange={(e) => setNewWord((p) => ({ ...p, label: e.target.value }))} />
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={newWord.is_regex} onChange={(e) => setNewWord((p) => ({ ...p, is_regex: e.target.checked }))} />
              Regex
            </label>
            <button type="submit" className="admin-btn primary">Add Rule</button>
          </form>
          <div className="admin-table-card embedded">
            <table>
              <thead><tr><th>Pattern</th><th>Label</th><th>Type</th><th></th></tr></thead>
              <tbody>
                {words.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: '#909399' }}>No filter rules configured</td></tr>
                ) : words.map((w) => (
                  <tr key={w.id}>
                    <td><code>{w.pattern}</code></td>
                    <td>{w.label || '—'}</td>
                    <td>{w.is_regex ? 'Regex' : 'Plain'}</td>
                    <td><button type="button" className="admin-btn sm danger" onClick={() => removeWord(w.id)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AllowedEmailDomainsModal
        open={domainModalOpen}
        onClose={() => setDomainModalOpen(false)}
        onChanged={loadAllowedDomainCount}
      />

      {dirty && (
        <div className="admin-alert-banner warning" style={{ marginTop: 16 }}>
          <span>⚠️</span>
          <div>
            <strong>Unsaved changes.</strong> Toggle changes are not saved until you click &quot;Save Security Settings&quot;.
          </div>
        </div>
      )}

      <div className="admin-settings-btn-group" style={{ marginTop: 16 }}>
        <button type="button" className="admin-btn primary" disabled={saving || !dirty} onClick={saveSecuritySettings}>
          {saving ? 'Saving…' : 'Save Security Settings'}
        </button>
        <button type="button" className="admin-btn secondary" disabled={!dirty} onClick={load}>Discard Changes</button>
      </div>
    </AdminLayout>
  )
}
